import AuthorizationService from '@app/model/authentication/service/AuthorizationService';
import useSiteState from '@app/state/SiteState';
import React, { useEffect, useRef, useState } from 'react';
import { IntercomProvider } from 'react-use-intercom';
import AppRouter from '@app/site/AppRouter';
import TagManager from 'react-gtm-module';
import * as Sentry from '@sentry/react';

const Application = React.memo(() => {

    const [principalReady, setPrincipalReady] = useState(() => !useSiteState.getState().token);
    const principalRequest = useRef<Promise<void> | null>(null);
    useEffect(() => {
        const token = useSiteState.getState().token;
        if (!token) { setPrincipalReady(true); return; }
        if (!principalRequest.current) {
            principalRequest.current = AuthorizationService.findCurrentPrincipalWithHeader(token)
                .then(principal => {
                    if (useSiteState.getState().token !== token) return;
                    if (principal) useSiteState.getState().setPrincipal(principal);
                    else useSiteState.getState().logout();
                })
                .catch(() => {
                    if (useSiteState.getState().token === token) useSiteState.getState().logout();
                });
        }
        let active = true;
        principalRequest.current.finally(() => { if (active) setPrincipalReady(true); });
        return () => { active = false; };
    }, []);

	const INTERCOM_APP_ID = 'ds9ml7e2';

	useEffect(() => {
		const tagManagerArgs = {
			gtmId: 'GTM-KBW7GCKK'
		};
		TagManager.initialize(tagManagerArgs);
	}, []);

	if (!principalReady) return <div role="status" className="p-8 text-center text-gray-500">Loading your account…</div>;

	return <Sentry.ErrorBoundary
		fallback={({ error, resetError }) => (
			<div className="flex items-center justify-center min-h-screen bg-gray-100">
				<div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
					<h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
					<p className="text-gray-700 mb-4">
						We're sorry, but something unexpected happened. Our team has been notified and we're working on fixing it.
					</p>
					<details className="mb-4">
						<summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
							Error details
						</summary>
						<pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-auto max-h-40">
							{error?.toString()}
						</pre>
					</details>
					<button
						onClick={resetError}
						className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors"
					>
						Try again
					</button>
				</div>
			</div>
		)}
		showDialog={false}
	>
		<IntercomProvider appId={INTERCOM_APP_ID}
											 autoBoot
											 autoBootProps={{
												 alignment: 'left',
												 verticalPadding: 100,
												 horizontalPadding: 40,
											 }}>
			<AppRouter />
		</IntercomProvider>
	</Sentry.ErrorBoundary>;
});

export default Application;